<script setup lang="ts">
import { useExtractedDatesStore } from '@/store/extractedDatesStore';
import { ExtractedDate } from '@/types/extractedDate';
import { NitteCalendarEvent } from '@WombatTechnology/nitte-time-lib';
import { storeToRefs } from 'pinia';
import { ref, toRefs, computed } from 'vue';
import type { CalendarType } from './CalendarSettingModal.vue';

interface Props {
  extractedDate: ExtractedDate
}
const props = defineProps<Props>()
const { extractedDate } = toRefs(props)

const extractedDatesStore = useExtractedDatesStore()
const { cachedEvents, isLoadingEvents } = storeToRefs(extractedDatesStore)
const CALENDAR_STORAGE_KEY = 'nitte-text-receive-calendar-type'

const getCalendarType = (): CalendarType => {
  const saved = localStorage.getItem(CALENDAR_STORAGE_KEY)
  if (saved === 'google' || saved === 'outlook' || saved === 'apple') return saved
  return 'google'
}
const isTouchDevice = window.matchMedia('(pointer: coarse)').matches
const showPopover = ref(false)
const isOnCalendarBtn = ref(false)
const isPartialExpanded = ref(false)
const activeTimeSlotPicker = ref<string | null>(null)
let hoverTimer: ReturnType<typeof setTimeout> | null = null

const isPartial = computed(() => extractedDate.value.availability === 'partial')
const isBusy = computed(() => extractedDate.value.availability === 'busy')

// busy時: 重複予定名をチップに直接表示
const busyEventNames = computed(() => {
  const d = extractedDate.value
  if (!d.start || !d.end || d.availability !== 'busy') return ''
  const names = cachedEvents.value
    .filter((ev: NitteCalendarEvent) => ev.period.start < d.end! && ev.period.end > d.start!)
    .map((ev: NitteCalendarEvent) => ev.title || '(タイトルなし)')
  return names.join(', ')
})

// labelから時間部分のみ抽出: "4月2日(木) 12:00-13:00" → "12:00-13:00"
const timeLabel = computed(() => {
  const label = extractedDate.value.label
  const match = label.match(/^.+?\(.+?\)\s+(.+)$/)
  return match ? match[1] : label
})

const fmtTime = (d: Date) =>
  d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false })

const fmtTimeStr = (iso: string) => fmtTime(new Date(iso))

type TimelineItem = {
  time: string
  label: string
  highlight: boolean
  color: 'green' | 'red' | ''
}

const timelineItems = computed((): TimelineItem[] => {
  const d = extractedDate.value
  if (!d.start || !d.end || !d.availability) return []

  const cStart = d.start
  const cEnd = d.end

  const sorted = cachedEvents.value
    .filter((ev: NitteCalendarEvent) => !ev.isAllDay)
    .map((ev: NitteCalendarEvent) => ({ title: ev.title, start: ev.period.start, end: ev.period.end }))
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  if (d.availability === 'free') {
    const items: TimelineItem[] = []
    const before = sorted.filter((e) => e.start < cStart)
    if (before.length > 0) {
      const prev = before[before.length - 1]
      items.push({ time: `${fmtTime(prev.start)}-${fmtTime(prev.end)}`, label: prev.title || '(タイトルなし)', highlight: false, color: '' })
    }
    items.push({ time: `${fmtTime(cStart)}-${fmtTime(cEnd)}`, label: '空き', highlight: true, color: 'green' })
    const after = sorted.filter((e) => e.start >= cEnd)
    if (after.length > 0) {
      const next = after[0]
      items.push({ time: `${fmtTime(next.start)}-${fmtTime(next.end)}`, label: next.title || '(タイトルなし)', highlight: false, color: '' })
    }
    return items
  } else if (d.availability === 'partial') {
    const items: TimelineItem[] = []
    for (const p of d.availablePeriods) {
      items.push({ time: `${fmtTimeStr(p.start)}-${fmtTimeStr(p.end)}`, label: '空き', highlight: true, color: 'green' })
    }
    const overlapping = sorted.filter((e) => e.start < cEnd && e.end > cStart)
    for (const e of overlapping) {
      items.push({ time: `${fmtTime(e.start)}-${fmtTime(e.end)}`, label: e.title || '(タイトルなし)', highlight: true, color: 'red' })
    }
    items.sort((a, b) => a.time.localeCompare(b.time))
    return items
  } else {
    // busy: 重複している予定
    return sorted
      .filter((e) => e.start < cEnd && e.end > cStart)
      .map((e) => ({ time: `${fmtTime(e.start)}-${fmtTime(e.end)}`, label: e.title || '(タイトルなし)', highlight: true, color: 'red' as const }))
  }
})

// partial展開用: 空き時間帯と重複予定をサブアイテムとして生成
type SubItem = { time: string; type: 'free' | 'busy'; label: string; start: Date; end: Date }

const partialSubItems = computed((): SubItem[] => {
  const d = extractedDate.value
  if (!d.start || !d.end || d.availability !== 'partial') return []

  const cStart = d.start
  const cEnd = d.end
  const items: SubItem[] = []

  for (const p of d.availablePeriods) {
    items.push({ time: `${fmtTimeStr(p.start)}-${fmtTimeStr(p.end)}`, type: 'free', label: '空き', start: new Date(p.start), end: new Date(p.end) })
  }

  const sorted = cachedEvents.value
    .filter((ev: NitteCalendarEvent) => !ev.isAllDay)
    .filter((ev: NitteCalendarEvent) => ev.period.start < cEnd && ev.period.end > cStart)
    .sort((a, b) => a.period.start.getTime() - b.period.start.getTime())

  for (const ev of sorted) {
    items.push({ time: `${fmtTime(ev.period.start)}-${fmtTime(ev.period.end)}`, type: 'busy', label: ev.title || '(タイトルなし)', start: ev.period.start, end: ev.period.end })
  }

  items.sort((a, b) => a.time.localeCompare(b.time))
  return items
})

// サブアイテム用タイムライン（ホバーポップオーバー）
const hoveredSubIdx = ref<number | null>(null)
let subHoverTimer: ReturnType<typeof setTimeout> | null = null

const subTimelineItems = computed((): TimelineItem[] => {
  if (hoveredSubIdx.value === null) return []
  const sub = partialSubItems.value[hoveredSubIdx.value]
  if (!sub) return []

  const allEvents = cachedEvents.value
    .filter((ev: NitteCalendarEvent) => !ev.isAllDay)
    .map((ev: NitteCalendarEvent) => ({ title: ev.title, start: ev.period.start, end: ev.period.end }))
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  if (sub.type === 'free') {
    const items: TimelineItem[] = []
    const before = allEvents.filter((e) => e.start < sub.start)
    if (before.length > 0) {
      const prev = before[before.length - 1]
      items.push({ time: `${fmtTime(prev.start)}-${fmtTime(prev.end)}`, label: prev.title || '(タイトルなし)', highlight: false, color: '' })
    }
    items.push({ time: `${fmtTime(sub.start)}-${fmtTime(sub.end)}`, label: '空き', highlight: true, color: 'green' })
    const after = allEvents.filter((e) => e.start >= sub.end)
    if (after.length > 0) {
      const next = after[0]
      items.push({ time: `${fmtTime(next.start)}-${fmtTime(next.end)}`, label: next.title || '(タイトルなし)', highlight: false, color: '' })
    }
    return items
  } else {
    return allEvents
      .filter((e) => e.start < sub.end && e.end > sub.start)
      .map((e) => ({ time: `${fmtTime(e.start)}-${fmtTime(e.end)}`, label: e.title || '(タイトルなし)', highlight: true, color: 'red' as const }))
  }
})

const onSubRowEnter = (idx: number) => {
  if (isTouchDevice) return
  // 親のポップオーバーを消す
  showPopover.value = false
  if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null }

  subHoverTimer = setTimeout(() => {
    if (!isOnCalendarBtn.value) {
      hoveredSubIdx.value = idx
    }
  }, 150)
}

const onSubRowLeave = () => {
  if (isTouchDevice) return
  if (subHoverTimer) {
    clearTimeout(subHoverTimer)
    subHoverTimer = null
  }
  hoveredSubIdx.value = null
}

const onRowEnter = () => {
  if (isTouchDevice) return
  if (!extractedDate.value.availability || isBusy.value || isPartial.value) return
  hoverTimer = setTimeout(() => {
    if (!isOnCalendarBtn.value) {
      showPopover.value = true
    }
  }, 150)
}

const onRowLeave = () => {
  if (isTouchDevice) return
  if (hoverTimer) {
    clearTimeout(hoverTimer)
    hoverTimer = null
  }
  showPopover.value = false
}

const onRowClick = () => {
  if (!isTouchDevice) return
  if (!extractedDate.value.availability || isBusy.value || isPartial.value) return
  showPopover.value = !showPopover.value
}

const hoveredCalBtn = ref<string | null>(null)

const onCalBtnEnter = (key = 'main') => {
  if (isTouchDevice) return
  isOnCalendarBtn.value = true
  showPopover.value = false
  hoveredCalBtn.value = key
}

const onCalBtnLeave = () => {
  if (isTouchDevice) return
  isOnCalendarBtn.value = false
  hoveredCalBtn.value = null
  if (!extractedDate.value.availability) return
  hoverTimer = setTimeout(() => {
    showPopover.value = true
  }, 150)
}

type TimeSlot = { start: Date; end: Date; label: string }

const generateTimeSlots = (start: Date, end: Date): TimeSlot[] | null => {
  const durationMs = end.getTime() - start.getTime()
  if (durationMs <= 60 * 60 * 1000) return null
  const slots: TimeSlot[] = []
  const lastStart = new Date(end.getTime() - 60 * 60 * 1000)
  let cursor = new Date(start)
  while (cursor <= lastStart) {
    const slotEnd = new Date(cursor.getTime() + 60 * 60 * 1000)
    slots.push({ start: new Date(cursor), end: slotEnd, label: `${fmtTime(cursor)}-${fmtTime(slotEnd)}` })
    cursor = new Date(cursor.getTime() + 30 * 60 * 1000)
  }
  return slots
}

const mainTimeSlots = computed(() => {
  const d = extractedDate.value
  if (!d.start || !d.end) return null
  return generateTimeSlots(d.start, d.end)
})

const onCalendarClick = (dateId: string) => {
  if (mainTimeSlots.value) {
    activeTimeSlotPicker.value = activeTimeSlotPicker.value === 'main' ? null : 'main'
  } else {
    extractedDatesStore.openCalendar(dateId, getCalendarType())
  }
}

const onTimeSlotSelect = (slot: TimeSlot) => {
  extractedDatesStore.openCalendarByTime(slot.start, slot.end, getCalendarType())
  activeTimeSlotPicker.value = null
}

const onSubCalendarClick = (start: Date, end: Date, idx: number) => {
  const slots = generateTimeSlots(start, end)
  if (slots) {
    const key = `sub-${idx}`
    activeTimeSlotPicker.value = activeTimeSlotPicker.value === key ? null : key
  } else {
    extractedDatesStore.openCalendarByTime(start, end, getCalendarType())
  }
}

const subTimeSlotsFor = (start: Date, end: Date) => generateTimeSlots(start, end)
</script>

<template>
  <div
    class="extracted-date-item"
    :class="{
      error: !extractedDate.success,
      'bg-free': extractedDate.availability === 'free',
      'bg-partial': isPartial,
      'bg-busy': extractedDate.availability === 'busy',
      'clickable': isPartial,
    }"
    @mouseenter="onRowEnter"
    @mouseleave="onRowLeave"
    @click="isPartial ? (isPartialExpanded = !isPartialExpanded) : onRowClick()"
  >
    <div class="item-main">
      <div class="item-text">
        <sup-label v-if="extractedDate.success" class="label">{{ timeLabel }}</sup-label>
        <p class="original-text">{{ extractedDate.original }}</p>
        <p v-if="!extractedDate.success" class="error-text">{{ extractedDate.error }}</p>
      </div>
      <div v-if="extractedDate.availability" class="chip-popover-anchor">
        <span
          class="status-chip"
          :class="{
            free: extractedDate.availability === 'free',
            partial: isPartial,
            busy: extractedDate.availability === 'busy',
          }"
        >
          <template v-if="extractedDate.availability === 'free'">空き<span class="chip-icon">○</span></template>
          <template v-else-if="isPartial">一部空き</template>
          <template v-else>{{ busyEventNames || '予定あり' }}<span class="chip-icon">×</span></template>
        </span>
        <!-- Timeline popover (free/busy only) -->
        <div v-if="showPopover && !isLoadingEvents && timelineItems.length > 0" class="timeline-popover">
          <div class="timeline">
            <div
              v-for="(item, idx) in timelineItems"
              :key="idx"
              class="timeline-row"
            >
              <div class="timeline-dot-col">
                <div
                  class="timeline-dot"
                  :class="{
                    'dot-green': item.highlight && item.color === 'green',
                    'dot-red': item.highlight && item.color === 'red',
                    'dot-gray': !item.highlight,
                  }"
                />
              </div>
              <div class="timeline-content">
                <div
                  class="timeline-time"
                  :class="{
                    'text-green': item.highlight && item.color === 'green',
                    'text-red': item.highlight && item.color === 'red',
                    'text-muted': !item.highlight,
                  }"
                >{{ item.time }}</div>
                <div
                  class="timeline-label"
                  :class="{
                    'text-green': item.highlight && item.color === 'green',
                    'text-red': item.highlight && item.color === 'red',
                    'text-sub': !item.highlight,
                  }"
                >{{ item.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Calendar button (free/busy) -->
      <div v-if="extractedDate.success && !isPartial" class="add-calendar-wrapper" :class="{ invisible: isBusy }" @mouseenter="onCalBtnEnter('main')" @mouseleave="onCalBtnLeave">
        <button class="add-calendar-button" @click.stop="onCalendarClick(extractedDate.id)">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M5 1v2M11 1v2M1 6h14M3 3h10a2 2 0 012 2v8a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div v-if="hoveredCalBtn === 'main' && activeTimeSlotPicker !== 'main'" class="cal-tooltip">カレンダーに登録</div>
        <div v-if="activeTimeSlotPicker === 'main' && mainTimeSlots" class="time-slot-picker" v-click-outside="() => activeTimeSlotPicker = null">
          <div class="time-slot-list">
            <button v-for="slot in mainTimeSlots" :key="slot.label" class="time-slot-item" @click.stop="onTimeSlotSelect(slot)">
              {{ slot.label }}
            </button>
          </div>
        </div>
      </div>
      <!-- Accordion toggle (partial only) -->
      <button v-if="isPartial" class="accordion-button" @click.stop="isPartialExpanded = !isPartialExpanded">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" :class="{ 'chevron-up': isPartialExpanded }">
          <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    <!-- Partial accordion: sub-items styled like normal items -->
    <div v-if="isPartialExpanded && isPartial && partialSubItems.length > 0" class="partial-accordion">
      <div
        v-for="(sub, idx) in partialSubItems"
        :key="idx"
        class="sub-item"
        :class="{ 'sub-free': sub.type === 'free', 'sub-busy': sub.type === 'busy' }"
        @mouseenter="onSubRowEnter(idx)"
        @mouseleave="onSubRowLeave"
        @click.stop="isTouchDevice && (hoveredSubIdx === idx ? (hoveredSubIdx = null) : (hoveredSubIdx = idx))"
      >
        <div class="sub-item-text">
          <sup-label class="label">{{ sub.time }}</sup-label>
        </div>
        <span v-if="sub.type === 'busy'" class="status-chip busy">{{ sub.label }}<span class="chip-icon">×</span></span>
        <div v-if="sub.type === 'free'" class="chip-popover-anchor">
          <span class="status-chip free">空き<span class="chip-icon">○</span></span>
          <div v-if="hoveredSubIdx === idx && !isOnCalendarBtn && subTimelineItems.length > 0" class="timeline-popover">
            <div class="timeline">
              <div v-for="(item, ti) in subTimelineItems" :key="ti" class="timeline-row">
                <div class="timeline-dot-col">
                  <div class="timeline-dot" :class="{ 'dot-green': item.highlight && item.color === 'green', 'dot-red': item.highlight && item.color === 'red', 'dot-gray': !item.highlight }" />
                </div>
                <div class="timeline-content">
                  <div class="timeline-time" :class="{ 'text-green': item.highlight && item.color === 'green', 'text-red': item.highlight && item.color === 'red', 'text-muted': !item.highlight }">{{ item.time }}</div>
                  <div class="timeline-label" :class="{ 'text-green': item.highlight && item.color === 'green', 'text-red': item.highlight && item.color === 'red', 'text-sub': !item.highlight }">{{ item.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="add-calendar-wrapper" :class="{ invisible: sub.type === 'busy' }" @mouseenter.stop="onCalBtnEnter('sub-' + idx); onSubRowLeave()" @mouseleave.stop="onCalBtnLeave">
          <button class="add-calendar-button" @click.stop="onSubCalendarClick(sub.start, sub.end, idx)">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M5 1v2M11 1v2M1 6h14M3 3h10a2 2 0 012 2v8a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div v-if="hoveredCalBtn === 'sub-' + idx && activeTimeSlotPicker !== 'sub-' + idx" class="cal-tooltip">カレンダーに登録</div>
          <div v-if="activeTimeSlotPicker === 'sub-' + idx && subTimeSlotsFor(sub.start, sub.end)" class="time-slot-picker" v-click-outside="() => activeTimeSlotPicker = null">
              <div class="time-slot-list">
              <button v-for="slot in subTimeSlotsFor(sub.start, sub.end)!" :key="slot.label" class="time-slot-item" @click.stop="onTimeSlotSelect(slot)">
                {{ slot.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.extracted-date-item {
  padding: 10px 12px;
  border-radius: var(--border-radius-6px);
  border: 1px solid #e5e7eb;
}
@media (max-width: 800px) {
  .extracted-date-item {
    padding: 14px 14px;
  }
}
.extracted-date-item.bg-free {
  background: #f4f9ee;
}
.extracted-date-item.bg-busy {
  background: #fdf2f2;
}
.extracted-date-item.bg-partial {
  background: #fffdf5;
}
.extracted-date-item.clickable {
  cursor: pointer;
}
.extracted-date-item.error {
  opacity: 0.7;
}
.item-main {
  display: flex;
  align-items: center;
  gap: 8px;
}
.item-text {
  flex: 1;
  min-width: 0;
}
.label {
  display: block;
}
@media (max-width: 800px) {
  .label {
    font-size: 1.125rem;
  }
}
.original-text {
  font-size: 0.8125rem;
  color: var(--font-gray-2);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 800px) {
  .original-text {
    font-size: 0.875rem;
    margin-top: 4px;
  }
}
.error-text {
  font-size: 0.8125rem;
  color: var(--error);
  margin-top: 4px;
}
/* Chip */
.chip-popover-anchor {
  position: relative;
  flex-shrink: 0;
}
.status-chip {
  flex-shrink: 0;
  display: inline-block;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  user-select: none;
  cursor: default;
}
.status-chip.free {
  background: #EAF3DE;
  color: #3B6D11;
}
.status-chip.partial {
  background: #FFF8E1;
  color: #8D6E00;
}
.status-chip.busy {
  background: #FCEBEB;
  color: #A32D2D;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chip-icon {
  margin-left: 2px;
  font-size: 0.625rem;
}
/* Accordion toggle */
.accordion-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: none;
  color: var(--font-gray-3);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s ease;
}
.accordion-button:hover {
  color: var(--main);
}
.accordion-button svg {
  transition: transform 0.2s ease;
}
.accordion-button .chevron-up {
  transform: rotate(180deg);
}
/* Partial accordion sub-items */
.partial-accordion {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sub-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: var(--border-radius-6px);
}
.sub-item.sub-free {
  background: #f4f9ee;
}
.sub-item.sub-busy {
  background: #fdf2f2;
}
.sub-item-text {
  flex: 1;
  min-width: 0;
}
/* Timeline popover */
.timeline-popover {
  position: absolute;
  bottom: calc(100% + 4px);
  right: 0;
  background: white;
  border: 0.5px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  z-index: 200;
  pointer-events: none;
  width: 200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
}
.timeline {
  display: flex;
  flex-direction: column;
}
.timeline-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding-bottom: 10px;
}
.timeline-row:last-child {
  padding-bottom: 0;
}
.timeline-dot-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 6px;
  padding-top: 3px;
  flex-shrink: 0;
}
.timeline-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-green {
  background: #3B6D11;
  border: 1px solid #3B6D11;
}
.dot-red {
  background: #A32D2D;
  border: 1px solid #A32D2D;
}
.dot-gray {
  background: white;
  border: 1px solid #d1d5db;
}
.timeline-content {
  flex: 1;
  min-width: 0;
}
.timeline-time {
  font-size: 0.6875rem;
  margin-bottom: 1px;
}
.timeline-label {
  font-size: 0.75rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.text-green {
  color: #3B6D11;
  font-weight: 600;
}
.text-red {
  color: #A32D2D;
  font-weight: 600;
}
.text-muted {
  color: #6b7280;
  font-weight: 400;
}
.text-sub {
  color: #6b7280;
  font-weight: 400;
}
/* Add calendar button */
.add-calendar-wrapper {
  position: relative;
  flex-shrink: 0;
}
.add-calendar-wrapper.invisible {
  visibility: hidden;
}
.add-calendar-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: none;
  color: var(--font-gray-3);
  cursor: pointer;
  transition: color 0.15s ease;
}
@media (max-width: 800px) {
  .add-calendar-button {
    width: 44px;
    height: 44px;
  }
}
.add-calendar-button:hover {
  color: var(--main);
}
/* Time slot picker */
.time-slot-picker {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 0.5px solid #d1d5db;
  border-radius: 8px;
  padding: 8px;
  z-index: 300;
  width: 160px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
}
.time-slot-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 200px;
  overflow-y: auto;
}
.time-slot-item {
  display: block;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 4px;
  background: none;
  font-size: 0.8125rem;
  color: var(--font-black);
  cursor: pointer;
  text-align: left;
  transition: background-color 0.1s ease;
}
.time-slot-item:hover {
  background: #f0f7e6;
}
.cal-tooltip {
  position: absolute;
  bottom: calc(100% + 2px);
  right: 0;
  background: #333;
  color: white;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 200;
}
</style>
